import type { ParentChildAssignment, ParentChildAssignmentResponse } from '@repo/types/parent';

import { isPast, parseISO } from 'date-fns';
import { CheckIcon, ClockIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';

export interface ParentAssignmentState {
  /** The child's most recent submission, or null when they never handed it in. */
  submission: ParentChildAssignmentResponse | null;
  isSubmitted: boolean;
  isOverdue: boolean;
  /** Parsed `dueTo`; null for an assignment with no deadline at all. */
  dueDate: Date | null;
}

/**
 * Everything the card and its dialog say about where one assignment stands.
 *
 * Derived in one place so the row and the dialog opened from it can never
 * disagree — a parent comparing the two would otherwise have no way of telling
 * which is right.
 *
 * Only work that was never handed in counts as overdue; a late submission still
 * reads as submitted.
 */
export function getParentAssignmentState(assignment: ParentChildAssignment): ParentAssignmentState {
  const submission = getLatestSubmission(assignment.assignemntresps);
  const dueDate = assignment.dueTo ? parseISO(assignment.dueTo) : null;
  const isSubmitted = submission !== null;

  return {
    submission,
    isSubmitted,
    isOverdue: dueDate !== null && !isSubmitted && isPast(dueDate),
    dueDate,
  };
}

/**
 * The backend filters submissions to this child, so anything in the array is
 * theirs and the newest of them is what their standing is judged on.
 */
function getLatestSubmission(submissions: ParentChildAssignmentResponse[] | undefined): ParentChildAssignmentResponse | null {
  if (!submissions?.length) return null;

  return submissions.reduce((latest, submission) => (Date.parse(submission.createdAt) >= Date.parse(latest.createdAt) ? submission : latest));
}

interface ParentAssignmentStatusBadgeProps {
  state: ParentAssignmentState;
}

/**
 * Oddane / Po terminie / Nieoddane, taken straight from the derived state so the
 * wording and the colour cannot be assigned by two different rules.
 */
export function ParentAssignmentStatusBadge(parentAssignmentStatusBadgeProps: ParentAssignmentStatusBadgeProps) {
  const { isSubmitted, isOverdue } = parentAssignmentStatusBadgeProps.state;

  return (
    <Badge size={'sm'} variant={isSubmitted ? 'success' : isOverdue ? 'destructive' : 'muted'}>
      {isSubmitted ? <CheckIcon /> : <ClockIcon />}
      {isSubmitted ? 'Oddane' : isOverdue ? 'Po terminie' : 'Nieoddane'}
    </Badge>
  );
}
